import React, { useState } from 'react';
// import { saveAs } from 'file-saver';
import './App.css';
import './themes.css';
import { getThemeClass } from './utils/assetLoader';
import EncryptionForm from './components/EncryptionForm';
import IPFSUpload from './components/IPFSUpload';
import DecryptionForm from './components/DecryptionForm';
import SafetyPlan from './components/SafetyPlan';
import DomesticViolenceResources from './components/DomesticViolenceResources';
import GunViolenceResources from './components/GunViolenceResources';
import PhilosophicalResources from './components/PhilosophicalResources';
import DecentralizedSharing from './components/DecentralizedSharing';
import QuickExit from './components/QuickExit';
import VideoBackground from './components/VideoBackground';
import Logo from './components/Logo';
import { encryptJSON } from './utils/encryption';

function App() {
  // Information tracking history
  // const [incidentHistory, setIncidentHistory] = useState([]);
  
  // Current tab being displayed
  const [currentSection, setCurrentSection] = useState('safety-plan');
  
  // IPFS and encryption states
  const [showIPFSUpload, setShowIPFSUpload] = useState(false);
  const [showEncryptionForm, setShowEncryptionForm] = useState(false);
  const [showDecryptionForm, setShowDecryptionForm] = useState(false);
  const [encryptedData, setEncryptedData] = useState(null);
  
  // Function to handle encryption with the provided key
  const handleEncrypt = (data, key) => {
    const encryptionResult = encryptJSON(data, key);
    
    if (encryptionResult.success) {
      // Save encrypted data for potential sharing via DecentralizedSharing
      try {
        const documentId = `encrypted_doc_${Date.now()}`;
        let encryptedDocs = {};
        
        try {
          const existingDocs = localStorage.getItem('encryptedDocuments');
          if (existingDocs) {
            encryptedDocs = JSON.parse(existingDocs);
          }
        } catch (parseError) {
          console.error('Error parsing existing encrypted documents, resetting:', parseError);
        }
        
        // Store reference to the encrypted document
        encryptedDocs[documentId] = {
          timestamp: new Date().toISOString(),
          data: encryptionResult.data,
          // Don't store the key for security reasons
          preview: {
            type: 'encrypted_safety_documentation',
            encrypted_at: new Date().toISOString(),
            incident_count: data.incidents?.current?.length || 0
          }
        };
        
        // Log for debugging
        console.log(`Adding encrypted doc with ID: ${documentId}`);
        console.log('Updated encrypted docs object:', Object.keys(encryptedDocs));
        
        // Store back to localStorage
        localStorage.setItem('encryptedDocuments', JSON.stringify(encryptedDocs));
        console.log(`Encrypted document saved to localStorage with ID: ${documentId}`);
        
        // Verify it was stored
        setTimeout(() => {
          const verification = localStorage.getItem('encryptedDocuments');
          if (verification) {
            console.log('Verified encrypted docs in localStorage:', 
              Object.keys(JSON.parse(verification)).length, 'documents');
          } else {
            console.error('Failed to verify encrypted docs in localStorage');
          }
        }, 100);
      } catch (error) {
        console.error('Error saving encrypted document reference:', error);
      }
      
      setEncryptedData(encryptionResult.data);
      setShowEncryptionForm(false);
      setShowIPFSUpload(true);
    } else {
      alert(`Encryption failed: ${encryptionResult.error}`);
    }
  };

  // Get the appropriate theme class for this domain
  const themeClass = getThemeClass();
  
  return (
    <div className={`survivor-app ${themeClass}`}>
      {/* Header with Quick Exit */}
      <header className="header">
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Video background for header */}
          <VideoBackground placement="header" />
          
          <div className="header-content">
            <div className="logo-container">
              <Logo />
              <h1>Survivor Resource Center</h1>
              <p>Document, secure, and heal.</p>
            </div>
            
            <div className="quick-exit-container">
              <QuickExit />
            </div>
            
            {/* Navigation */}
            <div className="nav-tabs">
              <button
                className={`nav-tab ${currentSection === 'safety-plan' ? 'active' : ''}`}
                onClick={() => setCurrentSection('safety-plan')}
              >
                Safety Plan
              </button>
              <button
                className={`nav-tab ${currentSection === 'gun-violence' ? 'active' : ''}`}
                onClick={() => setCurrentSection('gun-violence')}
              >
                Gun Violence
              </button>
              <button
                className={`nav-tab ${currentSection === 'domestic-violence' ? 'active' : ''}`}
                onClick={() => setCurrentSection('domestic-violence')}
              >
                Domestic Violence
              </button>
              <button
                className={`nav-tab ${currentSection === 'philosophy' ? 'active' : ''}`}
                onClick={() => setCurrentSection('philosophy')}
              >
                Coping Framework
              </button>
              <button
                className={`nav-tab ${currentSection === 'sharing' ? 'active' : ''}`}
                onClick={() => setCurrentSection('sharing')}
              >
                Community Network
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="container">
        {/* Video background for main content - placed at the document level */}
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
          <VideoBackground placement="body" />
        </div>
        
        {/* Safety Plan Section */}
        {currentSection === 'safety-plan' && (
          <SafetyPlan 
            onEncrypt={(data) => {
              setShowEncryptionForm(true);
              // Store the data temporarily until the user provides an encryption key
              setEncryptedData(data);
            }} 
          />
        )}

        {/* Domestic Violence Resources */}
        {currentSection === 'domestic-violence' && (
          <DomesticViolenceResources />
        )}

        {/* Gun Violence Resources */}
        {currentSection === 'gun-violence' && (
          <GunViolenceResources />
        )}

        {/* Philosophical Framework */}
        {currentSection === 'philosophy' && (
          <PhilosophicalResources />
        )}
        
        {/* Decentralized Sharing */}
        {currentSection === 'sharing' && (
          <DecentralizedSharing />
        )}
      </div>

      {/* Information Section */}
      <div className="info-section-container">
        <div className="info-section">
          <div className="explanation-box">
            <h2 className="section-title">How Your Information Stays Secure</h2>
            <table className="info-table">
            <tbody>
              <tr>
                <th>Active</th>
                <th>Feature</th>
                <th>Description</th>
              </tr>
              <tr>
                <td>➤</td>
                <td><strong>Local Storage</strong></td>
                <td>All information stays on your device until you choose to export it</td>
              </tr>
              <tr>
                <td>➤</td>
                <td><strong>Encryption</strong></td>
                <td>When you encrypt your data, it uses AES-256 encryption which is highly secure</td>
              </tr>
              <tr>
                <td>➤</td>
                <td><strong>Privacy</strong></td>
                <td>You control who can access your information with your encryption key</td>
              </tr>
              <tr>
                <td>➤</td>
                <td><strong>Decentralized Storage</strong></td>
                <td>IPFS allows for secure, distributed storage without central control</td>
              </tr>
            </tbody>
          </table>

            <div className="export-buttons">
              <button 
                onClick={() => setShowDecryptionForm(true)} 
                className="export-btn decrypt-btn"
              >
                <span className="export-icon">🔓</span> Decrypt Previously Secured Document
              </button>
            </div>
            
            {/* Encryption Form Modal */}
            {showEncryptionForm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <EncryptionForm 
                    onEncrypt={(key) => handleEncrypt(encryptedData, key)}
                    onCancel={() => {
                      setShowEncryptionForm(false);
                      setEncryptedData(null);
                    }}
                  />
                </div>
              </div>
            )}
            
            {/* IPFS Upload Modal */}
            {showIPFSUpload && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <IPFSUpload 
                    jsonData={encryptedData}
                    onClose={() => {
                      setShowIPFSUpload(false);
                      setEncryptedData(null);
                    }}
                    isEncrypted={true}
                  />
                </div>
              </div>
            )}
            
            {/* Decryption Form Modal */}
            {showDecryptionForm && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <DecryptionForm 
                    onClose={() => setShowDecryptionForm(false)}
                  />
                </div>
              </div>
            )}
          </div>
          
          <div className="template-box">
            <h2 className="section-title">Resources & Support</h2>
            <div className="support-resources">
              <p>
                This application is designed to help survivors document their experiences 
                and access resources. If you're in immediate danger, call 911 or your local 
                emergency number.
              </p>
              <div className="emergency-contacts">
                <div className="emergency-contact">
                  <h3>Domestic Violence Hotline</h3>
                  <p>1-800-799-7233</p>
                  <h4>Text START to 88788</h4>
                </div>
                <div className="emergency-contact">
                  <h3>Crisis Text Line</h3>
                  <p>Text HOME to 741741</p>
                </div>
                <div className="emergency-contact">
                  <h3>Suicide Prevention Lifeline</h3>
                  <p>988</p>
                </div>
              </div>
              <p className="philosophical-quote">
                Tell the truth. Live as a witness and not as a victim.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div style={{ marginBottom: '1rem' }}>
            <Logo />
          </div>
          <p>
            This tool is designed to help survivors document experiences and access resources.
            Your information remains private and secure.
          </p>
          <p>
            <small>
              All data is stored locally on your device unless you explicitly choose to export or encrypt it.
              This is not a substitute for emergency services or professional legal/medical advice.
            </small>
          </p>
          <p className="domain-indicator" style={{ fontSize: '0.7rem', opacity: 0.7 }}>
            Domain: {window.location.hostname}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;